<template>
    <div class="flex items-center justify-between text-primary">
        <div class="flex-1 sm:hidden">

            <PaginationShowing :meta="meta"/>
            <div class="flex justify-between mt-4">
                <button
                    :class="{'cursor-not-allowed opacity-50': !hasPrevious}"
                    class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-primary bg-white hover:text-primary focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                    @click.prevent="prev">
                    Previous
                </button>
                <button
                    :class="{'cursor-not-allowed opacity-50': !hasNext}"
                    class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-md text-primary bg-white hover:text-primary focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                    @click.prevent="next">
                    Next
                </button>
            </div>
        </div>
        <div
            class="hidden sm:flex-1 sm:flex sm:flex-col sm:space-y-6 sm:items-center md:space-y-0 md:flex-row md:justify-between">
            <div>
                <PaginationShowing :meta="meta"/>
            </div>
            <div>
                <nav class="relative z-0 inline-flex shadow-sm">
                    <button
                        :class="{'cursor-not-allowed opacity-50': !hasPrevious}"
                        aria-label="Previous"
                        class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm leading-5 font-medium text-primary hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                        @click.prevent="prev">
                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path clip-rule="evenodd"
                                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                  fill-rule="evenodd"/>
                        </svg>
                    </button>

                    <button
                        v-if="pageCount > 0"
                        :class="[currentPage === 1 ? 'text-white bg-primary hover:text-white': 'bg-white text-primary hover:text-primary']"
                        :disabled="currentPage === 1"
                        class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm leading-5 font-medium  focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                        @click.prevent="goTo(1)">
                        1
                    </button>

                    <button
                        v-if="showPrevMore"
                        class="group -ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm leading-5 font-medium text-primary"
                        @click.prevent="quickPrev">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                             xmlns="http://www.w3.org/2000/svg">
                            <path
                                class="group-hover:hidden"
                                d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"/>
                            <path
                                class="hidden group-hover:inline" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                stroke-linecap="round"
                                stroke-linejoin="round" stroke-width="2"></path>
                        </svg>
                    </button>

                    <button
                        v-for="page in pagers"
                        :class="[currentPage === page ? 'text-white bg-primary hover:text-white': 'bg-white text-primary hover:text-primary']"
                        :disabled="currentPage === page"
                        class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                        @click.prevent="goTo(page)">
                        {{ page }}
                    </button>

                    <button
                        v-if="showNextMore"
                        class="group -ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm leading-5 font-medium text-primary"
                        @click.prevent="quickNext">
                        <svg class=" w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                             xmlns="http://www.w3.org/2000/svg">
                            <path
                                class="group-hover:hidden"
                                d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"/>
                            <path
                                class="hidden group-hover:inline" d="M13 5l7 7-7 7M5 5l7 7-7 7" stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"></path>
                        </svg>
                    </button>

                    <button
                        v-if="pageCount > 1"
                        :class="[currentPage === pageCount ? 'text-white bg-primary hover:text-white': 'bg-white text-primary hover:text-primary']"
                        :disabled="currentPage === pageCount"
                        class="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                        @click.prevent="goTo(pageCount)">
                        {{ pageCount }}
                    </button>

                    <button :class="{'cursor-not-allowed opacity-50': !hasNext}"
                            aria-label="Next"
                            class="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-primary hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-primary transition ease-in-out duration-150"
                            @click.prevent="next">
                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path
                                clip-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                fill-rule="evenodd"/>
                        </svg>
                    </button>
                </nav>
            </div>
        </div>
    </div>
</template>

<script>
import PaginationShowing from "@app/Shared/Components/PaginationShowing";

export default {

    name: "PaginationLinks",

    props: {
        meta: {
            type: Object,
            required: true
        },
        pagerCount: {
            type: Number,
            default: 5
        }
    },

    data() {
        return {
            showPrevMore: false,
            showNextMore: false
        }
    },

    components: {
        PaginationShowing
    },

    methods: {
        goTo(page) {
            if (page > 0 && page <= this.pageCount && this.currentPage !== page) {
                this.$emit('change', page);
            }
        },
        prev() {
            this.goTo(this.currentPage - 1);
        },
        next() {
            this.goTo(this.currentPage + 1);
        },
        quickPrev() {
            this.goTo(this.currentPage - this.pagerCountOffset);
        },
        quickNext() {
            this.goTo(this.currentPage + this.pagerCountOffset);
        },
    },

    computed: {
        hasPrevious() {
            return this.currentPage > 1;
        },
        hasNext() {
            return this.currentPage < this.meta.last_page;
        },
        pagerCountOffset() {
            return this.pagerCount - 2;
        },
        pageCount() {
            return this.meta.last_page;
        },
        currentPage() {
            return this.meta.current_page;
        },
        pagers() {
            const halfPagerCount = (this.pagerCount - 1) / 2;

            let showPrevMore = false;
            let showNextMore = false;

            if (this.pageCount > this.pagerCount) {
                showPrevMore = this.currentPage > this.pagerCount - halfPagerCount;
                showNextMore = this.currentPage < this.pageCount - halfPagerCount;
            }

            const array = [];

            if (showPrevMore && !showNextMore) {
                const startPage = this.pageCount - (this.pagerCount - 2);
                for (let i = startPage; i < this.pageCount; i++) {
                    array.push(i);
                }
            } else if (!showPrevMore && showNextMore) {
                for (let i = 2; i < this.pagerCount; i++) {
                    array.push(i);
                }
            } else if (showPrevMore && showNextMore) {
                const offset = Math.floor(this.pagerCount / 2) - 1;
                for (let i = this.currentPage - offset; i <= this.currentPage + offset; i++) {
                    array.push(i);
                }
            } else {
                for (let i = 2; i < this.pageCount; i++) {
                    array.push(i);
                }
            }

            this.showPrevMore = showPrevMore;
            this.showNextMore = showNextMore;

            return array;
        }
    },
};

</script>
